/**
 * Get started — the public signup funnel (`/get-started`). No auth, no shell.
 *
 * Flow:
 *   'school'        — pick your STATE, then find your school (server-side
 *                     search of the national School Directory), then click
 *                     Continue → always 'contact'. Selection alone never
 *                     routes, and the list never reveals served vs not-yet.
 *   'contact'       — the universal question, asked no matter the school's
 *                     status: "What's your email?" + "I am a…" multi-select
 *                     (allowed combos: any single role, Parent+Teacher,
 *                     Parent+Administrator — see lib/get-started-roles).
 *                     Served (OPEN) schools hide "Other". Continue routes on
 *                     status + roles: OPEN → 'details' ('admin-contact' when
 *                     the pick is Administrator alone); SCHEDULED → POST
 *                     registration-interest, then 'scheduled'; anything else →
 *                     fire-and-forget SchoolInterestLead write (via
 *                     /api/public/school-interest), then either redirect
 *                     Administrators straight to the prefilled get-quote page
 *                     or route everyone else to 'unavailable' — a failed lead
 *                     write never blocks the funnel.
 *   'scheduled'     — reveal: we're serving the school but registration hasn't
 *                     opened; you're on the list for the opening date.
 *   'unavailable'   — reveal for NON-admins only (Administrators are sent
 *                     straight to the prefilled get-quote page from 'contact'):
 *                     Ordo isn't here yet + a prefilled "nudge your school"
 *                     mail draft carrying the quote link.
 *   'admin-contact' — reveal for Administrator-only at a served school: admin
 *                     accounts are invite-based, so the CTA is a drafted
 *                     support email requesting access.
 *   'details'       — name + cell phone + optional signup code + password.
 *                     Role and email were answered on 'contact'. (STUDENT can
 *                     self-register; ADMINISTRATOR alone can't.)
 *
 * The whole Create Account experience renders over a dimmed, non-interactive
 * menu backdrop (Airbnb-style). Submit POSTs `/api/auth/signup` twice: the
 * first call emails a 6-character verification code (inbox proof — signup is
 * invited-or-open-school only, and the code is what stops account squatting),
 * the second carries `otpCode` and completes the signup, after which we
 * immediately log in and drop into the app. PARENTS can enter an optional
 * signup code, then land on My Family with rostered students auto-linked
 * when the email/code matches.
 */

import { AutocompleteInput } from '@ordo/ui/autocomplete-input'
import { Button } from '@ordo/ui/button'
import { Card } from '@ordo/ui/card'
import { cn } from '@ordo/ui/cn'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogTitle } from '@ordo/ui/dialog'
import { Field, FieldLabel } from '@ordo/ui/field'
import { Input } from '@ordo/ui/input'
import { LoadingButton } from '@ordo/ui/loading-button'
import { PasswordInput } from '@ordo/ui/password-input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ordo/ui/select'
import { toast } from '@ordo/ui/sonner'
import { usePostHog } from '@posthog/react'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Check } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'

import { ExistingAccountAlert } from '@/components/existing-account-alert'
import { MenuPreviewBackdrop } from '@/components/menu-preview-backdrop'
import { PublicShell } from '@/components/public-shell'
import { formatPhone, isValidEmail, isValidPhone, signinHrefForEmail } from '@/lib/contact-fields'
import { GET_STARTED_ROLE_OPTIONS, senderRoleNoun, toggleRole } from '@/lib/get-started-roles'
import { formatRegistrationOpensAt } from '@/lib/registration-opens'
import { supportMailto } from '@/lib/support'
import { useAuth } from '@/stores/auth'
import { needsOnboarding, useProfile } from '@/stores/profile'

const GET_QUOTE_URL = 'https://www.ordo.com/get-quote'

/**
 * get-quote link with everything we know prefilled. The query params are the
 * contract with the marketing form (it may consume any subset): email,
 * school, city, state, src.
 */
function quoteUrl(school: { name: string; city: string | null; state: string | null }, email: string): string {
  const params = new URLSearchParams({ school: school.name })
  if (email.trim()) {
    params.set('email', email.trim())
  }
  if (school.city) {
    params.set('city', school.city)
  }
  if (school.state) {
    params.set('state', school.state)
  }
  params.set('src', 'app-get-started')
  return `${GET_QUOTE_URL}?${params.toString()}`
}

/**
 * NCES ships names in ALL CAPS ("FRANKLIN SCHOOL"). Title-case fully-upper
 * strings for display; leave naturally-cased names alone. Capitalizes after
 * start/space/hyphen/period/slash/paren but not apostrophes, so
 * "ST. MARY'S" → "St. Mary's".
 */
function displaySchoolName(name: string): string {
  if (name !== name.toUpperCase()) {
    return name
  }
  return name.toLowerCase().replace(/(^|[\s\-./(])([a-z])/g, (_m, sep: string, ch: string) => sep + ch.toUpperCase())
}

/**
 * Prefilled mail draft for nudging the school (recipient left blank — the
 * visitor knows their school office; we don't). Written as a warm peer
 * referral: the sender's identity leads with their ACTUAL role (captured a
 * step earlier — parent/teacher/student, or a role-free opener for "Other"),
 * claims stay to the sender's own impression (no words put in their mouth),
 * and the "available in <state>" line is honest — Ordo serves nationwide, it
 * just isn't at this school yet. The body carries the BARE get-quote link (no
 * email, no prefill params): a forwarded link can't leak who nudged, and a
 * clean URL doesn't read as spam the way a long query string does.
 */
function nudgeMessage(
  school: { name: string; city: string | null; state: string | null },
  roles: string[],
): { subject: string; body: string } {
  const stateName = school.state ? (US_STATE_NAMES[school.state.toUpperCase()] ?? school.state) : null
  const roleNoun = senderRoleNoun(roles)
  const intro = roleNoun ? `I'm a ${roleNoun} at ${school.name}` : `I'm part of the ${school.name} community`
  const subject = `Would ${school.name} consider Ordo for lunch?`
  const body = [
    'Hi,',
    '',
    `${intro}, and I wanted to pass along a school lunch program I'd love for us to offer — it's called Ordo. The menus looked great to me${stateName ? `, and it's available in ${stateName}` : ''}.`,
    '',
    'Would you be open to taking a look? You can see options and pricing for our school here:',
    GET_QUOTE_URL,
    '',
    'Thanks so much,',
    '[Your name]',
  ].join('\n')
  return { subject, body }
}

function nudgeMailto(school: { name: string; city: string | null; state: string | null }, roles: string[]): string {
  const { subject, body } = nudgeMessage(school, roles)
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}

/**
 * Drafted support email for an Administrator at a served school — admin
 * accounts are invite-based, so the ask goes to a human. The visitor's email
 * rides in the body (mailto can't set From, and they may send from another
 * inbox).
 */
function adminAccessMailto(school: { name: string; city: string | null; state: string | null }, email: string): string {
  const where = school.city && school.state ? ` (${school.city}, ${school.state})` : ''
  const subject = `School admin access for ${school.name}`
  const body = [
    'Hi Ordo team,',
    '',
    `I'm an administrator at ${school.name}${where} and I'd like school admin access to Ordo.`,
    '',
    `My email: ${email.trim()}`,
  ].join('\n')
  return supportMailto(subject, body)
}

/** USPS state codes → full names, so the state picker reads "Oregon" not "OR". */
const US_STATE_NAMES: Record<string, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
}

/** Map raw state values (codes or names) → display name, sorted A→Z by name. */
function toSortedStateOptions(states: string[]): Array<{ value: string; label: string }> {
  return states
    .map((s) => ({ value: s, label: US_STATE_NAMES[s.toUpperCase()] ?? s }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

/** All 50 states, alphabetical — the picker always shows every state. */
const ALL_STATE_OPTIONS = toSortedStateOptions(Object.keys(US_STATE_NAMES))

/** Registration status for a picked school (mirrors the server enum). */
type RegistrationStatus = 'OPEN' | 'SCHEDULED' | 'CLOSED'

interface SchoolHit {
  /** National School Directory id — always present, the option identity. */
  directoryEntryId: string
  name: string
  /** Slug of the claiming Ordo org; null until a customer org claims the school. */
  slug: string | null
  status: RegistrationStatus
  registrationOpensAt: string | null
  /** Timezone the schedule displays in (school's own) so every visitor sees the same wall clock. */
  registrationOpensTimeZone: string | null
  city: string | null
  state: string | null
}

async function searchSchools(state: string, q: string): Promise<SchoolHit[]> {
  if (!state) {
    return []
  }
  const params = new URLSearchParams({ state })
  if (q.trim().length >= 2) {
    params.set('q', q.trim())
  }
  const res = await fetch(`/api/public/schools?${params.toString()}`)
  if (!res.ok) {
    throw new Error('Could not search schools. Try again.')
  }
  const { schools } = (await res.json()) as { schools: SchoolHit[] }
  // Single normalization point: everything downstream (suggestions, labels,
  // stored state, quote links, mail drafts) gets naturally-cased names.
  return schools.map((s) => ({
    ...s,
    name: displaySchoolName(s.name),
    city: s.city ? displaySchoolName(s.city) : null,
  }))
}

/** "Lincoln Elementary (Portland, OR)" — falls back to just the name. */
function schoolLabel(s: SchoolHit): string {
  if (s.city && s.state) {
    return `${s.name} (${s.city}, ${s.state})`
  }
  if (s.state) {
    return `${s.name} (${s.state})`
  }
  return s.name
}

/**
 * Best-effort "does this email already have an account?" preflight for the
 * contact step. Fails soft (returns `false`) on any error so a hiccup never
 * traps the visitor — the signup `409` at the details step is the backstop.
 */
async function checkEmailExists(email: string): Promise<boolean> {
  try {
    const res = await fetch('/api/auth/email-exists', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email: email.trim() }),
    })
    if (!res.ok) {
      return false
    }
    const data = (await res.json().catch(() => ({}))) as { exists?: boolean }
    return data.exists === true
  } catch {
    return false
  }
}

type Step = 'school' | 'contact' | 'scheduled' | 'unavailable' | 'admin-contact' | 'details'

export function GetStartedPage() {
  const posthog = usePostHog()
  const navigate = useNavigate()
  const location = useLocation()
  // The current funnel step lives in HISTORY STATE, not useState, so the
  // browser Back button walks the funnel: every forward step pushes an entry,
  // and popping one lands on the step it left. An entry with no state (fresh
  // visit, reload) is the school picker.
  const step = (location.state as { step?: Step } | null)?.step ?? 'school'
  /** Forward navigation — push a history entry carrying the next step. */
  function advanceTo(next: Step) {
    void navigate('.', { state: { step: next } })
  }
  const [submitting, setSubmitting] = useState(false)
  // `errorCode` distinguishes the existing-account case (inline Alert +
  // "Log in" link), the invited-only rejection, and a bad verification
  // code from every other failure (generic message).
  const [errorCode, setErrorCode] = useState<
    'exists' | 'activation_code' | 'not_invited' | 'otp_invalid' | 'unexpected' | null
  >(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  // Email-verification step: set once the server has emailed the code
  // (fresh signups only — a RESUME is already signed in and skips it).
  const [otpRequired, setOtpRequired] = useState(false)
  const [otpCode, setOtpCode] = useState('')

  // Step 'school'
  const [stateCode, setStateCode] = useState('')
  const [query, setQuery] = useState('')
  const [school, setSchool] = useState<{
    directoryEntryId: string
    slug: string | null
    name: string
    status: RegistrationStatus
    registrationOpensAt: string | null
    registrationOpensTimeZone: string | null
    label: string
    city: string | null
    state: string | null
  } | null>(null)

  // Step 'contact' — the universal email + "I am a…" question. `email` and
  // `roles` are shared with the signup payload; the reveals ('scheduled',
  // 'unavailable', 'admin-contact') and the details form read them too.
  // Seeded from `?email=` (invite emails and /signin carry it over).
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState(() => searchParams.get('email')?.trim() ?? '')
  const [roles, setRoles] = useState<string[]>([])
  const [contactSubmitting, setContactSubmitting] = useState(false)
  const [contactError, setContactError] = useState<string | null>(null)
  // Set when the contact-step email is already registered — routes the visitor
  // to sign in instead of into a signup they can't complete.
  const [contactEmailExists, setContactEmailExists] = useState(false)

  // Step 'details'
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')
  const [activationCode, setActivationCode] = useState('')

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')

  // RESUME — a signed-in visitor is finishing a signup they never completed
  // (today: an account imported from the legacy ordo app, which holds their
  // old password but none of the profile this funnel collects). Their email
  // and password are already settled, so those inputs drop out and the name
  // is prefilled; school, roles, phone and the signup code are still asked
  // so nothing the funnel establishes is skipped.
  //
  // Gated on "signed in AND onboarding still outstanding", the same predicate
  // the `/` redirect and the server's resume branch use. A session alone is
  // not enough: an ALREADY-onboarded user who opens this page (bookmark, a
  // "Get started" link from their school, the back button after finishing)
  // must keep seeing the normal funnel — resume copy would be false for them,
  // and the server would reject the passwordless submit anyway.
  const { user: sessionUser } = useAuth()
  const { profile } = useProfile()
  const resuming = sessionUser != null && needsOnboarding(profile)
  const [prefilled, setPrefilled] = useState(false)
  const [deferring, setDeferring] = useState(false)

  /**
   * "Already have an account? Log in" — dropped while resuming, where the
   * visitor is already signed in and the link only loops them back to the
   * start of this funnel.
   */
  const loginFooter = resuming ? null : (
    <p className="border-t border-border pt-4 text-sm text-muted-foreground">
      Already have an account?{' '}
      <a href={signinHrefForEmail(email)} className="font-medium text-primary hover:underline">
        Log in
      </a>
    </p>
  )

  /**
   * Leave the funnel without finishing it. Only reachable from branches the
   * funnel cannot complete (school not on the new app, admin accounts are
   * invite-only): provisions the bare central User row so `/` stops
   * redirecting back here, then drops the user into the app's no-schools
   * page, which points at support.
   */
  async function deferOnboarding() {
    setDeferring(true)
    try {
      const res = await fetch('/api/auth/onboarding/defer', { method: 'POST' })
      if (!res.ok) {
        toast.error('Could not continue. Please try again.')
        return
      }
      window.location.href = '/'
    } catch {
      toast.error('Could not continue. Please try again.')
    } finally {
      setDeferring(false)
    }
  }

  useEffect(() => {
    if (!resuming || !sessionUser || prefilled) {
      return
    }
    setPrefilled(true)
    setEmail(sessionUser.email)
    const [first, ...rest] = (sessionUser.name ?? '').trim().split(/\s+/).filter(Boolean)
    if (first) {
      setFirstName((current) => current || first)
    }
    if (rest.length > 0) {
      setLastName((current) => current || rest.join(' '))
    }
  }, [resuming, sessionUser, prefilled])

  const isParent = roles.includes('PARENT')

  const { data: schools = [], isFetching } = useQuery({
    queryKey: ['public-schools', stateCode, query.trim()],
    queryFn: () => searchSchools(stateCode, query),
    // Don't preload the directory on state-select: only search once the visitor
    // types a real term (server returns [] without one anyway — PROD-8).
    enabled: stateCode.length > 0 && query.trim().length >= 2,
  })

  function pickByDirectoryEntryId(id: string) {
    const hit = schools.find((s) => s.directoryEntryId === id)
    if (!hit) {
      return
    }
    // Selection is inert — routing happens on Continue, and the list never
    // reveals served vs not-yet.
    setSchool({
      directoryEntryId: hit.directoryEntryId,
      slug: hit.slug,
      name: hit.name,
      status: hit.status,
      registrationOpensAt: hit.registrationOpensAt,
      registrationOpensTimeZone: hit.registrationOpensTimeZone,
      label: schoolLabel(hit),
      city: hit.city,
      state: hit.state,
    })
  }

  function handleContinue() {
    if (!school) {
      return
    }
    // Every school routes to the universal email + role question; that step's
    // Continue does the status/role branching. Keep any email/roles already
    // entered — re-picking a school shouldn't force retyping.
    setContactError(null)
    posthog.capture('signup_school_selected', {
      school_status: school.status,
      school_state: school.state ?? undefined,
    })
    advanceTo('contact')
  }

  // Served school + OPEN registration → the visitor can sign up (unless they
  // are an Administrator alone — admin accounts are invite-based).
  const servedOpen = school !== null && school.status === 'OPEN' && Boolean(school.slug)
  const adminOnly = roles.length === 1 && roles[0] === 'ADMINISTRATOR'

  // The universal question's Continue. First, on EVERY submission, check
  // whether the email already has an account — if so, prompt sign-in and stop
  // (no matter the school's status). Otherwise route by status: OPEN schools
  // go straight into the funnel (no lead write — the signup itself is the
  // capture); SCHEDULED blocks on the "keep me posted" write so the reveal
  // isn't a lie; unserved schools NEVER block on the write — losing a lead row
  // is better than losing the visitor at the door.
  async function handleContactContinue() {
    if (!school || !isValidEmail(email) || roles.length === 0) {
      return
    }
    setContactSubmitting(true)
    setContactError(null)
    setContactEmailExists(false)
    try {
      // Resuming: the account EXISTS by definition — it's the caller's own,
      // and they're signed into it. Running the preflight would bounce them
      // to a sign-in they've already done.
      if (!resuming && (await checkEmailExists(email))) {
        setContactEmailExists(true)
        return
      }

      posthog.capture('signup_contact_submitted', {
        roles,
        school_status: school.status,
      })

      if (servedOpen) {
        advanceTo(adminOnly ? 'admin-contact' : 'details')
        return
      }

      if (school.status === 'SCHEDULED') {
        const res = await fetch(
          `/api/public/schools/${encodeURIComponent(school.directoryEntryId)}/registration-interest`,
          {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ email: email.trim(), roles }),
          },
        )
        if (!res.ok) {
          const data = (await res.json().catch(() => ({}))) as { message?: string }
          setContactError(data.message ?? 'Something went wrong. Please try again.')
          return
        }
        advanceTo('scheduled')
        return
      }

      try {
        await fetch('/api/public/school-interest', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            email: email.trim(),
            directoryEntryId: school.directoryEntryId,
            roles,
          }),
        })
      } catch {
        // Swallow — we route on regardless of whether the lead write lands.
      }
      // Administrators are the buyers and already know Ordo isn't at their
      // school — skip the reveal and send them straight to the prefilled quote
      // form (email + school carried in the query). Everyone else gets the
      // 'unavailable' nudge card.
      // The quote form is for prospects. A RESUMING administrator is an
      // existing customer who is signed in right now — ejecting them to a
      // marketing page to request a quote for software their school already
      // bought (and leaving them with a live session and no profile) is
      // wrong. Send them to the same "not on the new Ordo yet" card as
      // everyone else, which has a way into the app.
      if (roles.includes('ADMINISTRATOR') && !resuming) {
        window.location.href = quoteUrl(school, email)
      } else {
        advanceTo('unavailable')
      }
    } catch {
      setContactError('Something went wrong. Please try again.')
    } finally {
      setContactSubmitting(false)
    }
  }

  const emailValid = isValidEmail(email)
  const phoneValid = isValidPhone(phone)

  // Only surface the "enter a valid email" hint once the user pauses typing,
  // not on every keystroke. Debounce 600ms after the last change.
  const [emailErrorVisible, setEmailErrorVisible] = useState(false)
  useEffect(() => {
    if (email.length === 0 || emailValid) {
      setEmailErrorVisible(false)
      return
    }
    const t = setTimeout(() => setEmailErrorVisible(true), 600)
    return () => clearTimeout(t)
  }, [email, emailValid])

  // Same debounced-hint pattern for the phone field.
  const [phoneErrorVisible, setPhoneErrorVisible] = useState(false)
  useEffect(() => {
    if (phone.length === 0 || phoneValid) {
      setPhoneErrorVisible(false)
      return
    }
    const t = setTimeout(() => setPhoneErrorVisible(true), 600)
    return () => clearTimeout(t)
  }, [phone, phoneValid])

  // Email + roles were validated by the 'contact' step's Continue gate.
  const detailsValid = firstName.trim().length > 0 && lastName.trim().length > 0 && phoneValid
  const passwordsMatch = password.length > 0 && password === confirm
  const passwordValid = password.length >= 8 && passwordsMatch
  // Resuming accounts already have a password — the fields aren't rendered,
  // so they must not gate the submit either. On the verify step the only
  // input is the emailed code.
  const signupValid = otpRequired ? otpCode.trim().length >= 6 : detailsValid && (resuming || passwordValid)

  // Back to the picker keeps the selection so the user can re-Continue.
  // History-based, so the in-UI back arrow and the browser Back button are
  // the same operation.
  function goBack() {
    void navigate(-1)
  }

  // Autocomplete suggestions: school name bold, "City, ST" as the muted
  // second line. The directory id rides along in `meta` for selection.
  const schoolSuggestions = schools.map((s) => ({
    label: s.name,
    value: [s.city, s.state].filter(Boolean).join(', '),
    meta: { directoryEntryId: s.directoryEntryId },
  }))

  async function handleCreate(includeCode: boolean) {
    setSubmitting(true)
    setErrorCode(null)
    setErrorMessage(null)
    try {
      const res = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          firstName,
          lastName,
          email: email.trim(),
          phone: phone.trim(),
          // Omitted when resuming — the account already has one, and the
          // server skips both the password check and multipass creation.
          ...(resuming ? {} : { password }),
          roles,
          schoolSlug: school?.slug ?? '',
          activationCode: isParent ? activationCode.trim() : '',
          ...(includeCode && otpCode.trim() ? { otpCode: otpCode.trim() } : {}),
        }),
      })
      const data = (await res.json().catch(() => ({}))) as {
        ok?: boolean
        otpRequired?: boolean
        code?: string
        error?: string
      }
      if (!res.ok || !data.ok) {
        setErrorCode(
          data.code === 'exists' ||
            data.code === 'activation_code' ||
            data.code === 'not_invited' ||
            data.code === 'otp_invalid'
            ? data.code
            : 'unexpected',
        )
        setErrorMessage(data.error ?? null)
        return
      }
      // Phase 1 answered: the server emailed a verification code (inbox
      // proof). Swap the details form for the code entry.
      if (data.otpRequired) {
        setOtpRequired(true)
        setOtpCode('')
        return
      }
      posthog.capture('signup_completed', { roles, resumed: resuming })

      // Resuming users are ALREADY signed in — re-posting credentials would
      // need the password we never asked for. Their session is live, so drop
      // straight into the app.
      if (resuming) {
        window.location.href = isParent ? '/family' : '/'
        return
      }

      // Email verification is skipped — self-signups are auto-verified, so
      // log in immediately and drop into the app instead of showing a
      // "check your email" screen.
      const loginRes = await fetch('/api/auth/credentials', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      })
      const loginData = (await loginRes.json().catch(() => ({}))) as { ok?: boolean; redirectTo?: string }
      if (loginRes.ok && loginData.ok) {
        // Parents go straight to My Family (`/family`) to set up their
        // student(s); others land on their default home.
        window.location.href = isParent ? '/family' : (loginData.redirectTo ?? '/')
      } else {
        window.location.href = signinHrefForEmail(email)
      }
    } catch {
      setErrorCode('unexpected')
    } finally {
      setSubmitting(false)
    }
  }

  // ── Step 'scheduled' — reveal only; the 'contact' step captured the email
  // and role and blocked on the "keep me posted" write before routing here.
  if (step === 'scheduled' && school) {
    const opensAtLabel = formatRegistrationOpensAt(
      school.registrationOpensAt,
      school.registrationOpensTimeZone ?? undefined,
    )
    return (
      <PublicShell center>
        <Card className="flex w-full max-w-md flex-col gap-6 p-8 shadow-md">
          <header className="flex flex-col gap-1">
            <h1 className="text-2xl font-semibold tracking-tight">You’re on the list!</h1>
            <p className="text-sm text-muted-foreground">
              {opensAtLabel ? (
                <>
                  Registration for {school.label} opens <span className="font-medium">{opensAtLabel}</span>. We’ll email
                  you when it’s time to sign up.
                </>
              ) : (
                <>Registration for {school.label} opens soon. We’ll email you when it’s time to sign up.</>
              )}
            </p>
          </header>
          {/* Terminal card: it never creates the central User row, so a
              resuming user needs a way into the app or `/` sends them back
              through this funnel indefinitely. */}
          {resuming ? (
            <LoadingButton type="button" className="w-full" loading={deferring} onClick={() => void deferOnboarding()}>
              Continue to Ordo
            </LoadingButton>
          ) : null}
          {loginFooter}
        </Card>
      </PublicShell>
    )
  }

  // ── Step 'contact' — the universal email + "I am a…" question ───────────
  // Asked for EVERY school, served or not (neutral ask — no status reveal,
  // no bad news yet). Continue does the status/role routing.
  if (step === 'contact' && school) {
    // 'OTHER' is normally hidden at a served school to keep new visitors on a
    // role that routes somewhere. A RESUMING legacy user may genuinely be
    // none of Parent/Teacher/Administrator/Student — cafeteria and kitchen
    // staff are a real slice of the old app's accounts — and hiding it would
    // force them to claim a role they don't hold, which then drives student
    // claims and their landing page.
    const roleOptions = GET_STARTED_ROLE_OPTIONS.filter((r) => !(servedOpen && !resuming && r.value === 'OTHER'))
    return (
      <PublicShell center>
        <Card className="flex w-full max-w-md flex-col gap-6 p-8 shadow-md">
          <header>
            <h1 className="text-2xl font-semibold tracking-tight">
              {resuming ? 'Welcome back — what brings you to Ordo?' : 'What’s your email?'}
            </h1>
            {resuming ? (
              <p className="mt-2 text-sm text-muted-foreground">
                Finishing setup for {email}. Your password hasn’t changed.
              </p>
            ) : null}
          </header>

          <form
            className="flex flex-col gap-6"
            onSubmit={(e) => {
              e.preventDefault()
              void handleContactContinue()
            }}>
            {resuming ? null : (
              <Field>
                <Input
                  id="contact-email"
                  aria-label="Email"
                  placeholder="you@example.com"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    // Editing the address dismisses a stale "already exists" alert.
                    setContactEmailExists(false)
                    setContactError(null)
                  }}
                />
                {emailErrorVisible ? <p className="text-xs text-destructive">Enter a valid email address.</p> : null}
              </Field>
            )}

            <Field>
              <FieldLabel>I am a…</FieldLabel>
              <div className="grid grid-cols-2 gap-3">
                {roleOptions.map((r, index) => {
                  const selected = roles.includes(r.value)
                  return (
                    <button
                      key={r.value}
                      type="button"
                      // Resume hides the email Input, which was this step's
                      // only autoFocus — without moving it, arriving here
                      // leaves focus on <body>, so the step change is never
                      // announced and keyboard users tab in from the top.
                      autoFocus={resuming && index === 0}
                      aria-pressed={selected}
                      onClick={() => setRoles((prev) => toggleRole(prev, r.value))}
                      className={cn(
                        'flex items-center gap-2.5 rounded-xl border px-4 py-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        selected
                          ? 'border-foreground/30 bg-muted/60 text-foreground shadow-sm'
                          : 'border-border bg-background text-foreground hover:bg-muted/40',
                      )}>
                      <span
                        aria-hidden
                        className={cn(
                          'flex size-5 shrink-0 items-center justify-center rounded-[4px] border transition-colors',
                          selected
                            ? 'border-foreground bg-foreground text-background'
                            : 'border-muted-foreground/40 bg-transparent',
                        )}>
                        {selected ? <Check className="size-3.5" /> : null}
                      </span>
                      {r.label}
                    </button>
                  )
                })}
              </div>
              {contactError ? <p className="text-xs text-destructive">{contactError}</p> : null}
            </Field>

            {contactEmailExists ? <ExistingAccountAlert /> : null}

            <LoadingButton
              type="submit"
              className="w-full"
              disabled={!emailValid || roles.length === 0}
              loading={contactSubmitting}>
              Continue
            </LoadingButton>
          </form>

          {loginFooter}
        </Card>
      </PublicShell>
    )
  }

  // ── Step 'admin-contact' — Administrator alone at a served school ───────
  // Admin accounts are invite-based (no self-signup), so the CTA is a
  // drafted support email requesting access.
  if (step === 'admin-contact' && school) {
    return (
      <PublicShell center>
        <Card className="flex w-full max-w-md flex-col gap-6 p-8 shadow-md">
          <header className="flex flex-col gap-1">
            <h1 className="text-2xl font-semibold tracking-tight">Let’s get you admin access</h1>
            <p className="text-sm text-muted-foreground">
              School admin accounts for {school.label} are set up by our support team. Send us a note — we’ve drafted it
              for you — and we’ll get you access.
            </p>
          </header>
          <Button asChild className="w-full">
            <a href={adminAccessMailto(school, email)}>Email support</a>
          </Button>
          {/* A resuming user is already signed in: this card cannot create
              their profile, so without a way out they would bounce between
              `/` and this funnel forever. Let them into the app (no-schools
              page + support) while their admin access is sorted out. */}
          {resuming ? (
            <LoadingButton
              type="button"
              variant="outline"
              className="w-full"
              loading={deferring}
              onClick={() => void deferOnboarding()}>
              Continue to Ordo
            </LoadingButton>
          ) : (
            loginFooter
          )}
        </Card>
      </PublicShell>
    )
  }

  // ── Step 'unavailable' — reveal only, and only for NON-admins; the
  // 'contact' step captured the email + role (fire-and-forget lead write)
  // before routing here, and Administrators were redirected to the prefilled
  // quote form rather than landing on this card. The CTA is a ready-to-send
  // nudge whose body carries the quote link.
  if (step === 'unavailable' && school) {
    // A RESUMING user is an existing Ordo customer from the previous app, so
    // "Ordo isn't at your school yet" and "ask your school to add Ordo" are
    // both false for them — their school HAD Ordo; it just hasn't moved to
    // the new app yet. They get honest copy and a way into the app instead
    // of a marketing nudge and a dead end.
    if (resuming) {
      return (
        <PublicShell center>
          <Card className="flex w-full max-w-md flex-col gap-6 p-8 shadow-md">
            <header className="flex flex-col gap-1">
              <h1 className="text-2xl font-semibold tracking-tight">{school.name} isn’t on the new Ordo yet</h1>
              <p className="text-sm text-muted-foreground">
                Your account has moved over, but we’re still bringing {school.name} across. We’ll email you the moment
                ordering opens — there’s nothing you need to do.
              </p>
            </header>
            <div className="flex flex-col gap-2">
              <LoadingButton
                type="button"
                className="w-full"
                loading={deferring}
                onClick={() => void deferOnboarding()}>
                Continue to Ordo
              </LoadingButton>
              <Button asChild variant="outline" className="w-full">
                <a href={supportMailto(`My school (${school.name}) isn’t on the new Ordo yet`)}>Contact support</a>
              </Button>
            </div>
          </Card>
        </PublicShell>
      )
    }
    return (
      <PublicShell center>
        <Card className="flex w-full max-w-md flex-col gap-6 p-8 shadow-md">
          <header className="flex flex-col gap-1">
            <h1 className="text-2xl font-semibold tracking-tight">Ordo isn’t at your school yet</h1>
            <p className="text-sm text-muted-foreground">
              Thanks! We’ll email you if that changes. Schools add Ordo when their community asks, so send yours a quick
              note.
            </p>
          </header>

          <div className="flex flex-col gap-2">
            <Button asChild className="w-full">
              <a href={nudgeMailto(school, roles)}>Nudge your school</a>
            </Button>
            {/* The mailto silently no-ops without a configured mail handler
                (in-app browsers, kiosks) — give those visitors a path that
                always works + visible feedback (cmrav3cay000h014l3htb4vv3). */}
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => {
                const { subject, body } = nudgeMessage(school, roles)
                void navigator.clipboard
                  .writeText(`Subject: ${subject}\n\n${body}`)
                  .then(() => toast.success('Message copied — paste it into an email to your school office.'))
                  .catch(() => toast.error('Couldn’t copy — long-press or select the text in your email app instead.'))
              }}>
              Copy the message instead
            </Button>
          </div>
          <p className="border-t border-border pt-4 text-sm text-muted-foreground">
            Already have an account?{' '}
            <a href={signinHrefForEmail(email)} className="font-medium text-primary hover:underline">
              Log in
            </a>
          </p>
        </Card>
      </PublicShell>
    )
  }

  // ── Step 'school' — the selector, centered on the public shell ──────────
  if (step === 'school' || !school) {
    return (
      <PublicShell center>
        <Card className="flex w-full max-w-md flex-col gap-6 p-8 shadow-md">
          <header className="flex flex-col gap-1">
            <h1 className="text-2xl font-semibold tracking-tight">
              {resuming ? 'Welcome back to Ordo' : 'Find your school'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {resuming
                ? 'We’ve rebuilt Ordo, and your sign-in still works. Confirm a few details and we’ll reconnect your account — start with your school.'
                : 'Search for your school to get started.'}
            </p>
          </header>

          <form
            className="flex flex-col gap-6"
            onSubmit={(e) => {
              e.preventDefault()
              handleContinue()
            }}>
            {/* Step 1 — pick a state (names collide nationwide). */}
            <Select
              value={stateCode || undefined}
              onValueChange={(v) => {
                setStateCode(v)
                setQuery('')
                setSchool(null)
              }}>
              <SelectTrigger>
                <SelectValue placeholder="Select your state" />
              </SelectTrigger>
              <SelectContent side="bottom" avoidCollisions={false}>
                {ALL_STATE_OPTIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Step 2 — type-to-search school input (server-side search; large
                catalog). No status shown — served vs not-yet look identical.
                Typing again clears the selection so Continue can't route to a
                school that no longer matches the text. */}
            {stateCode ? (
              <AutocompleteInput
                placeholder="Search by school name"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value)
                  setSchool(null)
                }}
                suggestions={schoolSuggestions}
                shouldFilter={false}
                loading={isFetching}
                emptyMessage={query.trim().length < 2 ? 'Type to search…' : 'No schools found.'}
                onSelect={(suggestion) => {
                  const id = suggestion.meta?.['directoryEntryId']
                  if (id) {
                    pickByDirectoryEntryId(id)
                    setQuery(suggestion.label)
                  }
                }}
              />
            ) : null}

            {stateCode ? (
              <Button type="submit" className="w-full" disabled={!school}>
                Continue
              </Button>
            ) : null}
          </form>

          {loginFooter}
        </Card>
      </PublicShell>
    )
  }

  const title = resuming ? 'Finish setting up your account' : 'Create your account'
  let submitLabel = 'Create Account'
  if (resuming) {
    submitLabel = 'Finish Setup'
  } else if (otpRequired) {
    submitLabel = 'Verify & Create Account'
  }

  // ── Create Account — dimmed order-page backdrop + forced modal ──────────
  // No PublicShell here: the backdrop IS the full app shell (with its own
  // sidebar + Ordo logo), so PublicShell's top bar would double the logo.
  return (
    <>
      <MenuPreviewBackdrop />

      {/* Forced, non-dismissable modal. Its overlay dims the backdrop. */}
      <Dialog open>
        <DialogContent
          hideCloseButton
          aria-describedby={undefined}
          className="sm:max-h-[90vh] sm:max-w-xl"
          overlayClassName="backdrop-blur-none bg-black/50"
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}>
          <div className="-mx-6 flex shrink-0 items-center gap-2 border-b px-6 pb-4">
            <Button type="button" variant="ghost" size="icon" aria-label="Back" onClick={goBack}>
              <ArrowLeft className="size-4" />
            </Button>
            <DialogTitle className="flex-1 text-center text-base font-semibold">{title}</DialogTitle>
            {/* Spacer to keep the title visually centered against the back button. */}
            <span className="size-9" aria-hidden />
          </div>

          {/* Native form so Enter submits from any field; the flex classes keep
              DialogBody's scroll region working (DialogBody must stay a flex child). */}
          <form
            className="flex min-h-0 flex-1 flex-col"
            onSubmit={(e) => {
              e.preventDefault()
              void handleCreate(otpRequired)
            }}>
            <DialogBody className="flex flex-col gap-3 py-3">
              {errorCode === 'exists' ? (
                <ExistingAccountAlert email={email} />
              ) : errorCode === 'unexpected' ? (
                <p className="text-sm text-destructive">Unexpected error. Please try again.</p>
              ) : errorCode === 'activation_code' ? (
                <p className="text-sm text-destructive">{errorMessage ?? 'Signup code could not be verified.'}</p>
              ) : errorCode === 'not_invited' ? (
                <p className="text-sm text-destructive">
                  {errorMessage ?? 'This email hasn’t been invited to Ordo yet.'}
                </p>
              ) : errorCode === 'otp_invalid' ? (
                <p className="text-sm text-destructive">
                  {errorMessage ??
                    'That code is incorrect or has expired. Check the email we sent, or resend the code.'}
                </p>
              ) : null}

              {step === 'details' && otpRequired ? (
                <div className="flex flex-col gap-3">
                  <p className="text-sm text-muted-foreground">
                    We sent a 6-character code to <span className="font-medium text-foreground">{email.trim()}</span>.
                    Enter it below to finish creating your account.
                  </p>
                  <Field>
                    <FieldLabel htmlFor="signup-otp">Verification code</FieldLabel>
                    <Input
                      id="signup-otp"
                      autoComplete="one-time-code"
                      autoFocus
                      placeholder="K3JF7Q"
                      className="uppercase tracking-widest"
                      value={otpCode}
                      onChange={(e) => {
                        setOtpCode(e.target.value)
                        setErrorCode(null)
                      }}
                    />
                  </Field>
                  <p className="text-sm text-muted-foreground">
                    Didn’t get it?{' '}
                    <button
                      type="button"
                      onClick={() => void handleCreate(false)}
                      disabled={submitting}
                      className="font-medium text-primary hover:underline">
                      Resend code
                    </button>{' '}
                    or{' '}
                    <button
                      type="button"
                      onClick={() => {
                        setOtpRequired(false)
                        setOtpCode('')
                        setErrorCode(null)
                      }}
                      className="font-medium text-primary hover:underline">
                      go back
                    </button>
                    .
                  </p>
                </div>
              ) : null}

              {step === 'details' && !otpRequired ? (
                <div className="flex flex-col gap-3">
                  <div className="grid grid-cols-2 gap-3">
                    <Field>
                      <FieldLabel htmlFor="signup-first">First Name</FieldLabel>
                      <Input id="signup-first" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="signup-last">Last Name</FieldLabel>
                      <Input id="signup-last" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                    </Field>
                  </div>
                  <Field>
                    <FieldLabel htmlFor="signup-phone">Cell Phone Number</FieldLabel>
                    <Input
                      id="signup-phone"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      placeholder="(555) 555-5555"
                      value={phone}
                      onChange={(e) => setPhone(formatPhone(e.target.value))}
                    />
                    {phoneErrorVisible ? <p className="text-xs text-destructive">Enter a valid phone number.</p> : null}
                  </Field>
                  {isParent ? (
                    <Field>
                      <FieldLabel htmlFor="signup-activation-code">Signup Code (Optional)</FieldLabel>
                      <Input
                        id="signup-activation-code"
                        autoComplete="one-time-code"
                        value={activationCode}
                        onChange={(e) => setActivationCode(e.target.value)}
                        placeholder="ABCD-1234"
                      />
                      <p className="text-xs text-muted-foreground">
                        Please enter if you have students enrolled in free and reduced lunch. You may have received this
                        via email.
                      </p>
                    </Field>
                  ) : null}
                  {/* Resuming accounts keep the password they already signed in
                      with — asking again (or silently resetting it) would be a
                      worse experience than leaving it alone. */}
                  {resuming ? null : (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-3">
                      <Field>
                        <FieldLabel htmlFor="signup-password">Password</FieldLabel>
                        <PasswordInput
                          id="signup-password"
                          autoComplete="new-password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                        />
                        <p className="text-xs text-muted-foreground">At least 8 characters.</p>
                      </Field>
                      <Field>
                        <FieldLabel htmlFor="signup-confirm">Verify Password</FieldLabel>
                        <PasswordInput
                          id="signup-confirm"
                          autoComplete="new-password"
                          value={confirm}
                          onChange={(e) => setConfirm(e.target.value)}
                        />
                        {confirm.length > 0 && !passwordsMatch ? (
                          <p className="text-xs text-destructive">Passwords don’t match.</p>
                        ) : null}
                      </Field>
                    </div>
                  )}
                </div>
              ) : null}
            </DialogBody>

            <DialogFooter className="flex-col gap-3 border-t border-border sm:flex-col sm:[&>button]:w-full">
              <LoadingButton type="submit" disabled={!signupValid} loading={submitting} className="w-full">
                {submitLabel}
              </LoadingButton>
              {resuming ? null : (
                <p className="text-center text-sm text-muted-foreground">
                  Already have an account?{' '}
                  <a href={signinHrefForEmail(email)} className="font-medium text-primary hover:underline">
                    Log in
                  </a>
                </p>
              )}
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  )
}
